import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Heart, Users, Target, DollarSign, Trophy, Gift, ChevronRight,
  Loader2, CheckCircle, Building2, Mail, Lock, User, ArrowRight,
  Star, TrendingUp, LogOut, Clock, ChevronDown, Calendar,
  Plus, X, MapPin, Share2, Award, Banknote
} from 'lucide-react';
import PayrollGiving from '../components/employee/PayrollGiving';
import EmployeeMatchDashboard from '../components/employee/EmployeeMatchDashboard';
import DonationSuccessView from '../components/portal/DonationSuccessView';
import DonationModal from '../components/portal/DonationModal';
import StripeDonationModal from '../components/DonationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Progress Ring Component
const ProgressRing = ({ progress, size = 80, strokeWidth = 8, color = '#3B82F6' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-1000 ease-out" />
    </svg>
  );
};

const ImpactPortal = () => {
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const donationId = searchParams.get('donation_id');
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [error, setError] = useState(null);
  
  // Donation flow state
  const [activeView, setActiveView] = useState('home'); // home, campaigns, donate, employee-dashboard
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [donationAmount, setDonationAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  
  // Employee detection state
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [donorEmail, setDonorEmail] = useState('');
  const [donorName, setDonorName] = useState('');
  const [isEmployee, setIsEmployee] = useState(false);
  const [employeeVerified, setEmployeeVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [matchInfo, setMatchInfo] = useState(null);
  
  // Employee login state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [employeeUser, setEmployeeUser] = useState(null);
  const [myDonations, setMyDonations] = useState(null);
  
  // Employee dropdown & volunteer state
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showDirectLoginModal, setShowDirectLoginModal] = useState(false);
  const [directLoginEmail, setDirectLoginEmail] = useState('');
  const [directLoginPassword, setDirectLoginPassword] = useState('');
  const [directLoginError, setDirectLoginError] = useState('');
  const [volunteerHours, setVolunteerHours] = useState([]);
  const [volunteerOpportunities, setVolunteerOpportunities] = useState([]);
  const [showLogHoursModal, setShowLogHoursModal] = useState(false);
  const [donationFlowStep, setDonationFlowStep] = useState('choice'); // 'choice', 'guest', 'employee', 'payment'
  const [newHoursForm, setNewHoursForm] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: '',
    charity_name: ''
  });
  const [submittingHours, setSubmittingHours] = useState(false);
  
  // Badges & Shoutouts state
  const [myBadges, setMyBadges] = useState({ earned: [], available: [], total_points: 0 });
  const [publicShoutouts, setPublicShoutouts] = useState([]);
  
  // Payment state
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Embedded Stripe donation modal
  const [showStripeDonation, setShowStripeDonation] = useState(false);
  const [stripeDonationCharity, setStripeDonationCharity] = useState(null);

  // Team leaderboard + individual visibility
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [hideFromLeaderboard, setHideFromLeaderboard] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/portal/${businessId}/team-leaderboard`)
      .then(r => setTeamLeaderboard(r.data.teams || []))
      .catch(() => {});
  }, [businessId]);

  useEffect(() => {
    if (employeeUser) setHideFromLeaderboard(!!employeeUser.hide_from_leaderboard);
  }, [employeeUser]);

  const toggleLeaderboardVisibility = async () => {
    const next = !hideFromLeaderboard;
    setHideFromLeaderboard(next);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/${businessId}/leaderboard-visibility`, {
        email: employeeUser.email,
        hidden: next,
      });
      toast.success(next ? 'You are now hidden from the leaderboard' : 'You are back on the leaderboard');
      fetchPortalData();
    } catch (e) {
      setHideFromLeaderboard(!next);
      toast.error('Could not update your leaderboard setting');
    }
  };

  useEffect(() => {
    fetchPortalData();
    fetchPublicShoutouts();
    checkExistingSession();
  }, [businessId]);

  useEffect(() => {
    // Check for payment success (Every.org or Stripe)
    const paymentRef = donationId || sessionId;
    if (paymentRef) {
      checkPaymentStatus(paymentRef);
    }
  }, [sessionId, donationId]);
  
  // Check if user is already logged in (from landing page login)
  const checkExistingSession = async () => {
    try {
      const response = await api.get('/api/auth/me');
      if (response.data && response.data.role === 'employee' && response.data.business_id === businessId) {
        console.log('Found existing employee session:', response.data);
        setEmployeeUser(response.data);
        setDonorEmail(response.data.email);
        setDonorName(response.data.name);
        setIsEmployee(true);
        setEmployeeVerified(true);
        // Fetch their data
        fetchMyDonations();
        setTimeout(() => fetchVolunteerData(), 100);
      }
    } catch (err) {
      // Not logged in or session expired - that's okay for impact portal
      console.log('No existing session found');
    }
  };

  const fetchPortalData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/${businessId}`);
      setPortalData(response.data);
      setLoading(false);
    } catch (err) {
      setError('Portal not found');
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sid) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/donations/status/${sid}`);
      if (response.data.status === 'paid') {
        setPaymentSuccess(true);
        setPaymentData(response.data);
        // Refresh portal data
        fetchPortalData();
      }
    } catch (err) {
      console.error('Payment status check failed:', err);
    }
  };

  const verifyEmployee = async () => {
    if (!donorEmail) return;
    setVerifying(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/portal/verify-employee`, {
        email: donorEmail,
        business_id: businessId
      });
      setIsEmployee(response.data.is_employee);
      setEmployeeVerified(true);
      if (response.data.is_employee) {
        setMatchInfo(response.data.match_info);
      }
    } catch (err) {
      console.error('Verification failed:', err);
    } finally {
      setVerifying(false);
    }
  };

  const handleEmployeeLogin = async () => {
    setLoginError('');
    try {
      console.log('Attempting employee login for:', donorEmail);
      const response = await axios.post(`${BACKEND_URL}/api/portal/employee-login`, {
        email: donorEmail,
        password: loginPassword
      });
      console.log('Login response:', response.data);
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      setEmployeeUser(response.data.user);
      setShowLoginModal(false);
      setDonorName(response.data.user.name);
      fetchMyDonations();
      setTimeout(() => fetchVolunteerData(), 100);
    } catch (err) {
      console.error('Login error:', err);
      setLoginError(err.response?.data?.detail || 'Login failed');
    }
  };

  const handleDirectLogin = async () => {
    setDirectLoginError('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/portal/employee-login`, {
        email: directLoginEmail,
        password: directLoginPassword
      });
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      setEmployeeUser(response.data.user);
      setDonorEmail(directLoginEmail);
      setDonorName(response.data.user.name);
      setEmployeeVerified(true);
      setShowDirectLoginModal(false);
      setDirectLoginEmail('');
      setDirectLoginPassword('');
      // Fetch donation history and volunteer data
      fetchMyDonations();
      // Small delay to ensure cookie is set
      setTimeout(() => fetchVolunteerData(), 100);
    } catch (err) {
      setDirectLoginError(err.response?.data?.detail || 'Login failed. Check your email and password.');
    }
  };

  const fetchMyDonations = async () => {
    try {
      const response = await api.get('/api/donations/my-history');
      setMyDonations(response.data);
    } catch (err) {
      console.error('Failed to fetch donation history:', err);
    }
  };

  const fetchVolunteerData = async () => {
    try {
      // Fetch volunteer hours for the employee
      const hoursRes = await api.get('/api/volunteer/my-hours').catch(() => ({ data: { logs: [] } }));
      setVolunteerHours(hoursRes.data?.logs || []);
      
      // Fetch available volunteer opportunities
      const eventsRes = await axios.get(`${BACKEND_URL}/api/volunteer/events?business_id=${businessId}`).catch(() => ({ data: { events: [] } }));
      setVolunteerOpportunities(eventsRes.data?.events || []);
      
      // Fetch badges for the employee - try authenticated first, fallback to all badges
      try {
        const badgesRes = await api.get('/api/badges/my-badges');
        setMyBadges(badgesRes.data || { earned: [], available: [], total_points: 0 });
      } catch (badgeErr) {
        console.log('Fetching all badges as fallback');
        // Fetch all badges as available if auth fails
        const allBadgesRes = await axios.get(`${BACKEND_URL}/api/badges`);
        setMyBadges({ earned: [], available: allBadgesRes.data?.badges || [], total_points: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch volunteer data:', err);
    }
  };
  
  // Fetch public shoutouts (Wall of Fame)
  const fetchPublicShoutouts = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/shoutouts/public/${businessId}`);
      setPublicShoutouts(res.data.shoutouts || []);
    } catch (err) {
      console.error('Failed to fetch shoutouts:', err);
    }
  };

  const handleDonate = async () => {
    if (!selectedCampaign || !donorEmail) return;
    
    const amount = customAmount ? parseFloat(customAmount) : donationAmount;
    if (amount < 1) {
      alert('Minimum donation is $1');
      return;
    }
    
    // Open embedded Stripe donation modal instead of redirecting to Every.org
    const charityForDonation = {
      name: selectedCampaign.charity_name || 'Charity',
      ein: selectedCampaign.charity_ein || '',
      city: '',
      state: '',
    };
    setStripeDonationCharity(charityForDonation);
    setShowDonateModal(false);
    setShowStripeDonation(true);
  };

  const handleStripeDonationSuccess = (donationData) => {
    setShowStripeDonation(false);
    setStripeDonationCharity(null);
    setPaymentSuccess(true);
    setPaymentData({
      amount: donationData.amount,
      charity_name: donationData.charity_name,
      donation_id: donationData.donation_id,
    });
    // Refresh portal data
    fetchPortalData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Impact Portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Portal Not Found</h1>
          <p className="text-gray-500">The company portal you&apos;re looking for doesn&apos;t exist.</p>
        </div>
      </div>
    );
  }

  const { business, campaigns, stats, leaderboard } = portalData;
  const primaryColor = business.primary_color || '#3B82F6';
  const secondaryColor = business.secondary_color || '#1E40AF';
  
  // Separate active and completed campaigns
  const activeCampaigns = campaigns.filter(c => c.status === 'active');
  const completedCampaigns = campaigns.filter(c => c.status === 'completed');
  const hasCampaigns = campaigns.length > 0;
  
  // Get section visibility and order from business settings
  const showStats = business.show_stats !== false;
  const showAbout = business.show_about !== false;
  const showLeaderboard = business.show_leaderboard !== false;
  const showTestimonials = business.show_testimonials === true;
  const showFeatured = business.show_featured_section !== false && business.featured_campaign_ids?.length > 0;
  const showTrends = business.show_trends === true && (
    (business.charity_trends?.length > 0) || 
    (business.industry_stats?.length > 0) || 
    (business.news_items?.length > 0)
  );
  const sectionOrder = business.section_order || ['stats', 'about', 'trends', 'leaderboard', 'testimonials', 'featured', 'campaigns'];
  const testimonials = business.testimonials || [];
  const featuredCampaignIds = business.featured_campaign_ids || [];
  const charityTrends = business.charity_trends || [];
  const industryStats = business.industry_stats || [];
  const newsItems = business.news_items || [];
  const trendsTitle = business.trends_title || 'Charity Trends & Insights';

  // Payment Success View
  if (paymentSuccess && paymentData) {
    return (
      <DonationSuccessView
        paymentData={paymentData}
        primaryColor={primaryColor}
        businessId={businessId}
        business={business}
        onBack={() => {
          setPaymentSuccess(false);
          setPaymentData(null);
          window.history.replaceState({}, '', `/impact/${businessId}`);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {business.logo_url ? (
              <img src={business.logo_url} alt={business.name} className="h-24 w-auto object-contain max-w-[340px]" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
            ) : null}
            {!business.logo_url ? (
              <>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: primaryColor }}>
                  {business.name?.[0]}
                </div>
                <div>
                  <h1 className="font-bold text-lg text-gray-900">{business.name}</h1>
                  <p className="text-xs text-gray-500">Impact Portal</p>
                </div>
              </>
            ) : (
              <div className="hidden items-center gap-3" style={{ display: 'none' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: primaryColor }}>
                  {business.name?.[0]}
                </div>
                <div>
                  <h1 className="font-bold text-lg text-gray-900">{business.name}</h1>
                  <p className="text-xs text-gray-500">Impact Portal</p>
                </div>
              </div>
            )}
          </div>
          
          <nav className="flex items-center gap-2">
            <button
              onClick={() => setActiveView('home')}
              data-testid="portal-nav-home"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === 'home' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              style={activeView === 'home' ? { background: primaryColor } : {}}
            >
              Home
            </button>
            <button
              onClick={() => setActiveView('campaigns')}
              data-testid="portal-nav-campaigns"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === 'campaigns' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              style={activeView === 'campaigns' ? { background: primaryColor } : {}}
            >
              Campaigns
            </button>
            <button
              onClick={() => setActiveView('leaderboard')}
              data-testid="portal-nav-leaderboard"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === 'leaderboard' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              style={activeView === 'leaderboard' ? { background: primaryColor } : {}}
            >
              Leaderboard
            </button>
            {employeeUser && (
              <button
                onClick={() => setActiveView('my-impact')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeView === 'my-impact' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={activeView === 'my-impact' ? { background: primaryColor } : {}}
              >
                My Impact
              </button>
            )}
            {employeeUser && (
              <button
                onClick={() => setActiveView('my-matching')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                  activeView === 'my-matching' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={activeView === 'my-matching' ? { background: primaryColor } : {}}
              >
                <Gift className="w-4 h-4" />
                My Matching
              </button>
            )}
            {employeeUser && (
              <button
                onClick={() => setActiveView('payroll')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                  activeView === 'payroll' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={activeView === 'payroll' ? { background: primaryColor } : {}}
                data-testid="portal-payroll-tab"
              >
                <Banknote className="w-4 h-4" />
                Payroll Giving
              </button>
            )}
            {employeeUser && (
              <button
                onClick={() => setActiveView('my-badges')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                  activeView === 'my-badges' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={activeView === 'my-badges' ? { background: primaryColor } : {}}
              >
                <Trophy className="w-4 h-4" />
                My Badges
              </button>
            )}
            {employeeUser && (
              <button
                onClick={() => setActiveView('volunteer')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                  activeView === 'volunteer' ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={activeView === 'volunteer' ? { background: primaryColor } : {}}
              >
                <Clock className="w-4 h-4" />
                Volunteer
              </button>
            )}
          </nav>
          
          <div className="relative">
            {employeeUser ? (
              <>
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: primaryColor }}>
                    {employeeUser.name?.[0]}
                  </div>
                  <span className="text-sm font-medium text-gray-700">{employeeUser.name}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {/* User Dropdown */}
                {showUserDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserDropdown(false)} />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border z-50 py-2">
                      <div className="px-4 py-3 border-b">
                        <p className="text-sm font-semibold text-gray-900">{employeeUser.name}</p>
                        <p className="text-xs text-gray-500">{employeeUser.email}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full text-white" style={{ background: primaryColor }}>
                          Employee
                        </span>
                      </div>
                      
                      <div className="py-1">
                        <button 
                          onClick={() => { setActiveView('my-impact'); setShowUserDropdown(false); }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        >
                          <User className="w-4 h-4 text-gray-400" />
                          My Impact
                        </button>
                        <button 
                          onClick={() => { setActiveView('volunteer'); setShowUserDropdown(false); }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        >
                          <Clock className="w-4 h-4 text-gray-400" />
                          Volunteer Hours
                        </button>
                      </div>
                      
                      <div className="border-t py-1">
                        <button 
                          onClick={() => {
                            setEmployeeUser(null);
                            setEmployeeVerified(false);
                            setDonorEmail('');
                            setShowUserDropdown(false);
                            setActiveView('home');
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDirectLoginModal(true)}
                  data-testid="portal-sign-in-btn"
                  className="px-4 py-2 border rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-all flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Employee Sign In
                </button>
                {hasCampaigns && (
                <button
                  onClick={() => {
                    setShowDonateModal(true);
                    setSelectedCampaign(activeCampaigns[0] || completedCampaigns[0]);
                  }}
                  data-testid="portal-donate-now-btn"
                  className="px-5 py-2.5 text-white rounded-lg font-semibold transition-all hover:shadow-lg flex items-center gap-2"
                  style={{ background: primaryColor }}
                >
                <Heart className="w-4 h-4" /> Donate Now
              </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* HOME VIEW */}
      {activeView === 'home' && (
        <>
          {/* Hero Section - with configurable text position */}
          <section 
            className="relative min-h-[400px] flex items-center justify-center"
            style={{ 
              background: business.hero_style === 'image' && business.hero_image_url 
                ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${business.hero_image_url}) center/cover`
                : business.hero_style === 'minimal'
                  ? '#FAF7F2'
                  : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
            }}
          >
            <div 
              className="absolute px-8 max-w-2xl"
              style={{ 
                left: `${business.hero_text_position?.x || 50}%`, 
                top: `${business.hero_text_position?.y || 50}%`, 
                transform: 'translate(-50%, -50%)',
                textAlign: business.hero_text_align || 'center'
              }}
            >
              <h1 className={`text-4xl font-bold mb-4 ${business.hero_style === 'minimal' ? 'text-gray-900' : 'text-white'}`}>
                {business.hero_title || 'Making a Difference Together'}
              </h1>
              <p className={`text-xl mb-8 ${business.hero_style === 'minimal' ? 'text-gray-600' : 'text-white/90'}`}>
                {business.hero_subtitle || 'Join us in creating positive social impact'}
              </p>
              <button
                onClick={() => {
                  if (hasCampaigns) {
                    setShowDonateModal(true);
                    setSelectedCampaign(activeCampaigns[0] || completedCampaigns[0]);
                  }
                }}
                className={`px-8 py-3 rounded-full font-bold text-lg shadow-lg transition-all ${hasCampaigns ? 'hover:scale-105' : 'opacity-50 cursor-not-allowed'}`}
                style={{ 
                  background: business.hero_style === 'minimal' ? primaryColor : 'white',
                  color: business.hero_style === 'minimal' ? 'white' : primaryColor
                }}
              >
                {business.call_to_action_text || 'Donate Now'} <ArrowRight className="w-5 h-5 inline ml-2" />
              </button>
            </div>
          </section>

          {/* Dynamic Sections based on section_order */}
          {sectionOrder.map(sectionId => {
            switch (sectionId) {
              case 'stats':
                return showStats ? (
                  <section key="stats" className="py-12 px-4 bg-white">
                    <div className="max-w-4xl mx-auto grid grid-cols-4 gap-6">
                      {[
                        { value: `$${Math.round(stats.total_raised/1000)}K`, label: 'Total Raised', color: primaryColor },
                        { value: campaigns.length, label: 'Campaigns', color: '#10B981' },
                        { value: stats.total_donors || 0, label: 'Total Donors', color: '#8B5CF6' },
                        { value: `${Math.round((stats.total_raised/Math.max(campaigns.reduce((s,c) => s + (c.goal_amount || 0), 0), 1))*100)}%`, label: 'Goal Progress', color: '#F59E0B' }
                      ].map((stat, i) => (
                        <div key={i} className="text-center p-4 rounded-xl" style={{ background: `${stat.color}10` }}>
                          <p className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                          <p className="text-sm text-gray-600">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null;

              case 'about':
                return showAbout && business.about_text ? (
                  <section key="about" className="py-12 px-4" style={{ background: '#FAF7F2' }}>
                    <div className="max-w-4xl mx-auto">
                      <div className={`grid ${business.company_image_url ? 'grid-cols-2 gap-8 items-center' : ''}`}>
                        {business.company_image_url && (
                          <img src={business.company_image_url} alt="Company" className="rounded-xl shadow-lg" />
                        )}
                        <div>
                          <h2 className="text-2xl font-bold mb-4" style={{ color: primaryColor }}>About Us</h2>
                          {business.mission_statement && (
                            <p className="text-lg font-medium text-gray-700 mb-4 italic">&ldquo;{business.mission_statement}&rdquo;</p>
                          )}
                          <p className="text-gray-600 leading-relaxed">{business.about_text}</p>
                        </div>
                      </div>
                    </div>
                  </section>
                ) : null;

              case 'trends':
                return showTrends ? (
                  <section key="trends" className="py-16 px-4 bg-white">
                    <div className="max-w-5xl mx-auto">
                      <h2 className="text-2xl font-bold mb-2 text-center" style={{ color: primaryColor }}>
                        {trendsTitle}
                      </h2>
                      <p className="text-gray-500 text-center mb-10">Understanding the impact of corporate giving</p>
                      
                      {/* Industry Statistics */}
                      {industryStats.length > 0 && (
                        <div className="mb-12">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {industryStats.map((stat, i) => (
                              <div key={i} className="text-center p-6 rounded-xl" style={{ background: `${primaryColor}08` }}>
                                <p className="text-3xl font-bold mb-1" style={{ color: primaryColor }}>{stat.value}</p>
                                <p className="text-sm text-gray-600 mb-2">{stat.label}</p>
                                {stat.source && <p className="text-xs text-gray-400">{stat.source}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Charity Trends */}
                      {charityTrends.length > 0 && (
                        <div className="mb-12">
                          <h3 className="text-lg font-semibold mb-6 text-gray-800">Key Trends in Corporate Giving</h3>
                          <div className="grid md:grid-cols-3 gap-6">
                            {charityTrends.map((trend, i) => (
                              <div key={i} className="p-6 rounded-xl border" style={{ borderColor: '#E8DFD0' }}>
                                <div className="text-3xl mb-3">{trend.icon}</div>
                                <h4 className="font-semibold text-gray-900 mb-2">{trend.title}</h4>
                                <p className="text-sm text-gray-600">{trend.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* News Items */}
                      {newsItems.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold mb-6 text-gray-800">Latest News & Updates</h3>
                          <div className="space-y-4">
                            {newsItems.map((news, i) => (
                              <div key={i} className="flex gap-4 p-4 rounded-lg hover:bg-gray-50 transition-all">
                                <div className="text-sm text-gray-400 whitespace-nowrap">
                                  {new Date(news.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-1">{news.headline}</h4>
                                  <p className="text-sm text-gray-600">{news.summary}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                ) : null;

              case 'leaderboard':
                return showLeaderboard && leaderboard.length > 0 ? (
                  <section key="leaderboard" className="py-12 px-4 bg-white">
                    <div className="max-w-4xl mx-auto">
                      <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: primaryColor }}>
                        {business.leaderboard_title || 'Our Impact Champions'}
                      </h2>
                      <div className="grid grid-cols-5 gap-4">
                        {leaderboard.slice(0, 5).map((person, i) => (
                          <div key={i} className="text-center">
                            <div className={`relative mx-auto mb-3 ${i === 0 ? 'scale-110' : ''}`}>
                              <div 
                                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto"
                                style={{ background: i === 0 ? '#F59E0B' : i === 1 ? '#9CA3AF' : i === 2 ? '#CD7F32' : primaryColor }}
                              >
                                {person.name?.split(' ').map(n => n[0]).join('') || '?'}
                              </div>
                              {i < 3 && (
                                <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center">
                                  <span className="text-sm">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                                </div>
                              )}
                            </div>
                            <p className="font-semibold text-sm text-gray-900">{person.name?.split(' ')[0] || 'Anonymous'}</p>
                            <p className="text-xs text-gray-500">${person.total?.toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                ) : null;

              case 'testimonials':
                return showTestimonials && testimonials.length > 0 ? (
                  <section key="testimonials" className="py-12 px-4" style={{ background: '#FAF7F2' }}>
                    <div className="max-w-4xl mx-auto">
                      <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: primaryColor }}>
                        What People Say
                      </h2>
                      <div className="grid grid-cols-2 gap-6">
                        {testimonials.map((t, i) => (
                          <div key={i} className="bg-white rounded-xl p-6 shadow-md">
                            <div className="flex gap-1 mb-3">
                              {[1,2,3,4,5].map(star => (
                                <span key={star} className={`text-lg ${star <= (t.rating || 5) ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
                              ))}
                            </div>
                            <p className="text-gray-600 italic mb-4">&ldquo;{t.text}&rdquo;</p>
                            <div>
                              <p className="font-semibold text-gray-900">{t.name}</p>
                              <p className="text-sm text-gray-500">{t.role}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                ) : null;

              case 'featured':
                return showFeatured ? (
                  <section key="featured" className="py-16 px-4" style={{ background: `linear-gradient(135deg, ${primaryColor}08 0%, ${secondaryColor}15 100%)` }}>
                    <div className="max-w-5xl mx-auto">
                      <div className="text-center mb-10">
                        <span className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-3" 
                          style={{ background: `${primaryColor}15`, color: primaryColor }}>
                          ⭐ FEATURED
                        </span>
                        <h2 className="text-3xl font-bold" style={{ color: primaryColor }}>
                          {business.featured_section_title || 'Featured Causes'}
                        </h2>
                        <p className="text-gray-500 mt-2 max-w-lg mx-auto">
                          These are our top priority campaigns making the biggest impact right now.
                        </p>
                      </div>
                      <div className="grid grid-cols-3 gap-6">
                        {featuredCampaignIds.slice(0, 3).map((campaignId, index) => {
                          const campaign = campaigns.find(c => c.campaign_id === campaignId);
                          if (!campaign) return null;
                          const progress = Math.min((campaign.raised_amount || 0) / (campaign.goal_amount || 1) * 100, 100);
                          const impactValue = Math.round((campaign.raised_amount || 0) * (campaign.impact_conversion_rate || 1));
                          return (
                            <div 
                              key={campaignId} 
                              className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all hover:shadow-xl hover:-translate-y-1"
                              style={{ borderColor: index === 0 ? primaryColor : '#E8DFD0' }}
                            >
                              {index === 0 && (
                                <div className="px-4 py-2 text-center text-white text-sm font-semibold"
                                  style={{ background: primaryColor }}>
                                  🏆 TOP FEATURED
                                </div>
                              )}
                              <div className="p-6">
                                <div className="mb-4">
                                  <h3 className="font-bold text-lg text-gray-900 mb-1">{campaign.title}</h3>
                                  <p className="text-sm font-medium" style={{ color: secondaryColor }}>
                                    {campaign.charity_name}
                                  </p>
                                  {campaign.charity_ein && (
                                    <p className="text-xs text-gray-400 mt-1">EIN: {campaign.charity_ein}</p>
                                  )}
                                </div>
                                
                                {campaign.description && (
                                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{campaign.description}</p>
                                )}
                                
                                <div className="mb-4">
                                  <div className="flex justify-between text-sm mb-2">
                                    <span className="font-semibold text-gray-900">${(campaign.raised_amount || 0).toLocaleString()}</span>
                                    <span className="text-gray-500">of ${(campaign.goal_amount || 0).toLocaleString()}</span>
                                  </div>
                                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: primaryColor }} />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">{Math.round(progress)}% funded</p>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-2 mb-4 p-3 rounded-lg bg-gray-50">
                                  <div className="text-center">
                                    <p className="text-lg font-bold" style={{ color: primaryColor }}>{campaign.esg_score || '--'}</p>
                                    <p className="text-xs text-gray-500">ESG Score</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-lg font-bold text-green-600">{impactValue > 0 ? impactValue.toLocaleString() : '--'}</p>
                                    <p className="text-xs text-gray-500">{campaign.impact_metric || 'Impact'}</p>
                                  </div>
                                </div>
                                
                                {campaign.employer_match_enabled && (
                                  <div className="mb-4 p-2 rounded-lg text-center text-sm"
                                    style={{ background: `${secondaryColor}20`, color: secondaryColor }}>
                                    ✨ {campaign.employer_match_ratio || 1}x Employer Match Active
                                  </div>
                                )}
                                
                                <button 
                                  onClick={() => { setSelectedCampaign(campaign); setShowDonateModal(true); }}
                                  className="w-full py-3 rounded-xl font-bold text-white transition-all hover:opacity-90 hover:shadow-md"
                                  style={{ background: primaryColor }}
                                >
                                  💝 Donate Now
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                ) : null;

              case 'campaigns':
                return (
                  <section key="campaigns" className="py-12 px-4 bg-white">
                    <div className="max-w-5xl mx-auto">
                      {/* Active Campaigns */}
                      {activeCampaigns.filter(c => !featuredCampaignIds.includes(c.campaign_id)).length > 0 && (
                        <>
                          <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: primaryColor }}>
                            {showFeatured ? 'More Campaigns' : 'Active Campaigns'}
                          </h2>
                          <div className="grid grid-cols-2 gap-6 mb-10">
                            {activeCampaigns
                              .filter(c => !featuredCampaignIds.includes(c.campaign_id))
                              .slice(0, 4)
                              .map((campaign, i) => {
                                const progress = Math.min((campaign.raised_amount || 0) / (campaign.goal_amount || 1) * 100, 100);
                                return (
                                  <div key={campaign.campaign_id || i} className="border rounded-xl p-5 hover:shadow-lg transition-all bg-white" style={{ borderColor: '#E8DFD0' }}>
                                    <div className="flex justify-between items-start mb-3">
                                      <div>
                                        <h3 className="font-bold text-gray-900">{campaign.title}</h3>
                                        <p className="text-sm" style={{ color: secondaryColor }}>{campaign.charity_name}</p>
                                      </div>
                                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
                                    </div>
                                    {campaign.description && (
                                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{campaign.description}</p>
                                    )}
                                    <div className="mb-3">
                                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                                        <div className="h-full rounded-full" style={{ width: `${progress}%`, background: primaryColor }} />
                                      </div>
                                      <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">${(campaign.raised_amount || 0).toLocaleString()} raised</span>
                                        <span className="font-medium" style={{ color: primaryColor }}>{Math.round(progress)}%</span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => { setSelectedCampaign(campaign); setShowDonateModal(true); }}
                                      className="w-full py-2 text-white rounded-lg font-medium"
                                      style={{ background: primaryColor }}
                                    >
                                      Donate Now
                                    </button>
                                  </div>
                                );
                              })}
                          </div>
                        </>
                      )}

                      {/* Completed Campaigns */}
                      {completedCampaigns.length > 0 && (
                        <>
                          <h2 className="text-2xl font-bold mb-6 text-center text-gray-700">
                            Completed Campaigns
                          </h2>
                          <div className="grid grid-cols-2 gap-6">
                            {completedCampaigns
                              .slice(0, 4)
                              .map((campaign, i) => {
                                const progress = Math.min((campaign.raised_amount || 0) / (campaign.goal_amount || 1) * 100, 100);
                                return (
                                  <div key={campaign.campaign_id || i} className="border rounded-xl p-5 hover:shadow-lg transition-all bg-white" style={{ borderColor: '#d1d5db' }}>
                                    <div className="flex justify-between items-start mb-3">
                                      <div>
                                        <h3 className="font-bold text-gray-900">{campaign.title}</h3>
                                        <p className="text-sm" style={{ color: secondaryColor }}>{campaign.charity_name}</p>
                                      </div>
                                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Completed</span>
                                    </div>
                                    {campaign.description && (
                                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{campaign.description}</p>
                                    )}
                                    <div className="mb-3">
                                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                                        <div className="h-full rounded-full" style={{ width: `${Math.min(progress, 100)}%`, background: '#10B981' }} />
                                      </div>
                                      <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">${(campaign.raised_amount || 0).toLocaleString()} raised</span>
                                        <span className="font-medium text-emerald-600">{Math.round(progress)}%</span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => { setSelectedCampaign(campaign); setShowDonateModal(true); }}
                                      className="w-full py-2 text-white rounded-lg font-medium bg-emerald-600 hover:bg-emerald-700"
                                    >
                                      Donate More
                                    </button>
                                  </div>
                                );
                              })}
                          </div>
                        </>
                      )}

                      {/* No campaigns at all */}
                      {campaigns.length === 0 && (
                        <div className="text-center py-12">
                          <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <h3 className="text-lg font-semibold text-gray-600">No campaigns available</h3>
                          <p className="text-sm text-gray-400">Check back soon for new giving opportunities.</p>
                        </div>
                      )}
                      
                      {campaigns.length > 4 && (
                        <div className="text-center mt-8">
                          <button 
                            onClick={() => setActiveView('campaigns')}
                            className="px-6 py-2 rounded-full font-medium"
                            style={{ background: `${primaryColor}15`, color: primaryColor }}
                          >
                            View All Campaigns →
                          </button>
                        </div>
                      )}
                    </div>
                  </section>
                );

                              default:
                return null;
            }
          })}
          
          {/* Wall of Fame - Public Shoutouts (always shown if there are shoutouts) */}
          {publicShoutouts.length > 0 && (
            <section className="py-12 px-4" style={{ background: `linear-gradient(135deg, ${primaryColor}05 0%, ${secondaryColor}10 100%)` }}>
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <span className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-3"
                    style={{ background: `${primaryColor}15`, color: primaryColor }}>
                    🏆 WALL OF FAME
                  </span>
                  <h2 className="text-2xl font-bold" style={{ color: primaryColor }}>Team Recognition</h2>
                  <p className="text-gray-500 mt-2">Celebrating our amazing team members</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {publicShoutouts.slice(0, 6).map(shoutout => (
                    <div key={shoutout.shoutout_id} className="bg-white rounded-xl p-5 shadow-md border hover:shadow-lg transition-all">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full" style={{ background: `${primaryColor}15` }}>
                          {shoutout.type === 'volunteering' ? (
                            <Clock className="w-5 h-5" style={{ color: primaryColor }} />
                          ) : shoutout.type === 'donation' ? (
                            <Heart className="w-5 h-5 text-red-500" />
                          ) : (
                            <Star className="w-5 h-5 text-yellow-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {shoutout.employees?.map((emp, i) => (
                              <span key={i} className="font-semibold text-gray-900">{emp.name || 'Team Member'}</span>
                            ))}
                          </div>
                          <p className="text-gray-600 text-sm">{shoutout.message}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {shoutout.type === 'volunteering' ? '🤝 Volunteering' : shoutout.type === 'donation' ? '💝 Donation' : '⭐ Milestone'}
                            {' • '}{new Date(shoutout.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* CAMPAIGNS VIEW */}
      {activeView === 'campaigns' && (
        <section className="py-12 px-4 max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">All Campaigns</h2>
          <p className="text-gray-500 mb-8">{activeCampaigns.length} active • {completedCampaigns.length} completed</p>
          
          {/* Active Campaigns */}
          {activeCampaigns.length > 0 && (
            <>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span> Active Campaigns
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                {activeCampaigns.map(campaign => {
                  const progress = Math.min(((campaign.raised_amount || 0) / (campaign.goal_amount || 1)) * 100, 100);
                  return (
                    <div key={campaign.campaign_id} className="bg-white rounded-2xl shadow-sm border hover:shadow-lg transition-all overflow-hidden">
                      <div className="h-2" style={{ background: primaryColor }}></div>
                      <div className="p-6">
                        <h3 className="font-semibold text-lg text-gray-900 mb-1">{campaign.title}</h3>
                        <p className="text-sm text-gray-500 mb-2">{campaign.charity_name}</p>
                        {campaign.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{campaign.description}</p>
                        )}
                        <div className="flex items-center gap-4 mb-4">
                          <div className="relative">
                            <ProgressRing progress={progress} size={60} strokeWidth={6} color={primaryColor} />
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{progress.toFixed(0)}%</span>
                          </div>
                          <div>
                            <p className="text-xl font-bold" style={{ color: primaryColor }}>${(campaign.raised_amount || 0).toLocaleString()}</p>
                            <p className="text-xs text-gray-500">of ${(campaign.goal_amount || 0).toLocaleString()} goal</p>
                          </div>
                        </div>
                        {campaign.employer_match_enabled && (
                          <div className="bg-green-50 rounded-lg p-2 mb-4 flex items-center gap-2">
                            <Gift className="w-4 h-4 text-green-600" />
                            <span className="text-xs text-green-700 font-medium">
                              {campaign.employer_match_ratio || 1}:1 Employer Matching
                            </span>
                          </div>
                        )}
                        <button
                          onClick={() => { setSelectedCampaign(campaign); setShowDonateModal(true); }}
                          className="w-full py-3 text-white rounded-xl font-semibold transition-all hover:shadow-md"
                          style={{ background: primaryColor }}
                        >
                          Donate Now
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Completed Campaigns */}
          {completedCampaigns.length > 0 && (
            <>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Completed Campaigns
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {completedCampaigns.map(campaign => {
                  const progress = Math.min(((campaign.raised_amount || 0) / (campaign.goal_amount || 1)) * 100, 100);
                  return (
                    <div key={campaign.campaign_id} className="bg-white rounded-2xl shadow-sm border hover:shadow-lg transition-all overflow-hidden">
                      <div className="h-2 bg-emerald-500"></div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-lg text-gray-900">{campaign.title}</h3>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Completed</span>
                        </div>
                        <p className="text-sm text-gray-500 mb-2">{campaign.charity_name}</p>
                        {campaign.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{campaign.description}</p>
                        )}
                        <div className="flex items-center gap-4 mb-4">
                          <div className="relative">
                            <ProgressRing progress={Math.min(progress, 100)} size={60} strokeWidth={6} color="#10B981" />
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{progress.toFixed(0)}%</span>
                          </div>
                          <div>
                            <p className="text-xl font-bold text-emerald-600">${(campaign.raised_amount || 0).toLocaleString()}</p>
                            <p className="text-xs text-gray-500">of ${(campaign.goal_amount || 0).toLocaleString()} goal</p>
                          </div>
                        </div>
                        <button
                          onClick={() => { setSelectedCampaign(campaign); setShowDonateModal(true); }}
                          className="w-full py-3 text-white rounded-xl font-semibold transition-all hover:shadow-md bg-emerald-600 hover:bg-emerald-700"
                        >
                          Donate More
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      )}

      {/* LEADERBOARD VIEW */}
      {activeView === 'leaderboard' && (
        <section className="py-12 px-4 max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Trophy className="w-16 h-16 mx-auto mb-4" style={{ color: primaryColor }} />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Impact Leaderboard</h2>
            <p className="text-gray-500">Friendly competition, real impact</p>
          </div>

          {teamLeaderboard.length > 0 && (
            <div className="mb-10" data-testid="team-leaderboard">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Top teams</h3>
                <span className="text-xs text-gray-500">{teamLeaderboard.length} teams giving</span>
              </div>
              <div className="space-y-3">
                {teamLeaderboard.slice(0, 8).map((team, i) => {
                  const leader = teamLeaderboard[0].impact || 1;
                  return (
                    <div key={team.team} className="bg-white rounded-2xl shadow-sm border p-5" data-testid={`team-row-${i}`}>
                      <div className="flex items-center gap-4">
                        <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold ${
                          i === 0 ? 'bg-yellow-100 text-yellow-700' :
                          i === 1 ? 'bg-gray-200 text-gray-600' :
                          i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {team.rank}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-bold text-gray-900 truncate">{team.team}</p>
                            <p className="font-bold whitespace-nowrap" style={{ color: primaryColor }}>
                              ${Math.round(team.impact).toLocaleString()}
                            </p>
                          </div>
                          <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${Math.max((team.impact / leader) * 100, 4)}%`, background: primaryColor }} />
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            {team.givers} of {team.team_size} giving · {team.participation}% participation · {team.donations} gifts
                            {team.matched > 0 && <> · ${Math.round(team.matched).toLocaleString()} matched</>}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <h3 className="text-lg font-bold text-gray-900 mb-4">Top individuals</h3>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b flex items-center gap-4">
              <span className="w-8 text-sm font-medium text-gray-500">#</span>
              <span className="flex-1 text-sm font-medium text-gray-500">Donor</span>
              <span className="w-24 text-sm font-medium text-gray-500 text-right">Total</span>
              <span className="w-20 text-sm font-medium text-gray-500 text-right">Impact</span>
            </div>
            
            {leaderboard.map((donor, i) => (
              <div key={i} className={`p-6 flex items-center gap-4 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  i === 0 ? 'bg-yellow-100 text-yellow-700' : 
                  i === 1 ? 'bg-gray-200 text-gray-600' : 
                  i === 2 ? 'bg-orange-100 text-orange-700' : 
                  'bg-gray-100 text-gray-500'
                }`}>
                  {i + 1}
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{donor.name}</p>
                  <p className="text-xs text-gray-500">{donor.donations} donation{donor.donations !== 1 ? 's' : ''}</p>
                </div>
                <p className="w-24 font-bold text-right" style={{ color: primaryColor }}>${donor.total?.toLocaleString()}</p>
                <p className="w-20 font-bold text-right text-purple-600">
                  ${((donor.total || 0) + (donor.matched || 0)).toLocaleString()}
                </p>
              </div>
            ))}
            
            {leaderboard.length === 0 && (
              <div className="p-12 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Be the first donor!</p>
              </div>
            )}
          </div>

          {employeeUser && (
            <div className="mt-6 bg-white rounded-2xl border shadow-sm p-5 flex items-center justify-between gap-4" data-testid="leaderboard-visibility-card">
              <div>
                <p className="text-sm font-semibold text-gray-900">Show my name on the leaderboard</p>
                <p className="text-xs text-gray-500 mt-1">Turn this off to keep your giving private. Your team total still counts.</p>
              </div>
              <button
                onClick={toggleLeaderboardVisibility}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${hideFromLeaderboard ? 'bg-gray-300' : 'bg-emerald-500'}`}
                data-testid="leaderboard-visibility-toggle"
                aria-label="Toggle leaderboard visibility"
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${hideFromLeaderboard ? 'left-0.5' : 'left-6'}`} />
              </button>
            </div>
          )}
        </section>
      )}

      {/* MY IMPACT VIEW (Employee Dashboard) */}
      {activeView === 'my-impact' && employeeUser && myDonations && (
        <section className="py-12 px-4 max-w-5xl mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">My Impact Dashboard</h2>
            <p className="text-gray-500">Track your charitable giving and employer matching</p>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <DollarSign className="w-8 h-8 mb-2" style={{ color: primaryColor }} />
              <p className="text-3xl font-bold" style={{ color: primaryColor }}>${myDonations.stats.total_donated?.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Donated</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <Gift className="w-8 h-8 mb-2 text-green-600" />
              <p className="text-3xl font-bold text-green-600">${myDonations.stats.total_matched?.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Employer Matched</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <TrendingUp className="w-8 h-8 mb-2 text-purple-600" />
              <p className="text-3xl font-bold text-purple-600">${myDonations.stats.total_impact?.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Impact</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <Heart className="w-8 h-8 mb-2 text-pink-500" />
              <p className="text-3xl font-bold text-pink-500">{myDonations.stats.donation_count}</p>
              <p className="text-sm text-gray-500">Donations Made</p>
            </div>
          </div>
          
          {/* Donation History */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="font-semibold text-lg">Donation History</h3>
            </div>
            <div className="divide-y">
              {myDonations.donations.map((d, i) => (
                <div key={i} className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${primaryColor}20` }}>
                    <Heart className="w-5 h-5" style={{ color: primaryColor }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{d.campaign_title || 'Donation'}</p>
                    <p className="text-sm text-gray-500">{d.charity_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold" style={{ color: primaryColor }}>${d.amount?.toLocaleString()}</p>
                    {d.employer_match_amount > 0 && (
                      <p className="text-xs text-green-600">+${d.employer_match_amount} matched</p>
                    )}
                  </div>
                  <div className="text-right text-xs text-gray-400">
                    {new Date(d.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
              {myDonations.donations.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                  <p>No donations yet. Start making an impact!</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* PAYROLL GIVING VIEW */}
      {activeView === 'payroll' && employeeUser && (
        <section className="py-12 px-4 max-w-5xl mx-auto">
          <PayrollGiving
            businessId={businessId}
            campaigns={portalData?.campaigns || []}
            primaryColor={primaryColor}
          />
        </section>
      )}

      {/* VOLUNTEER HOURS VIEW */}
      {activeView === 'volunteer' && employeeUser && (
        <section className="py-12 px-4 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Volunteer Hours</h2>
              <p className="text-gray-500">Track and log your volunteer activities</p>
            </div>
            <button
              onClick={() => setShowLogHoursModal(true)}
              className="px-4 py-2 text-white rounded-xl font-semibold flex items-center gap-2 hover:shadow-lg transition-all"
              style={{ background: primaryColor }}
            >
              <Plus className="w-4 h-4" />
              Log Hours
            </button>
          </div>
          
          {/* Volunteer Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <Clock className="w-8 h-8 mb-2" style={{ color: primaryColor }} />
              <p className="text-3xl font-bold" style={{ color: primaryColor }}>
                {employeeUser.volunteer_hours_logged || 0}
              </p>
              <p className="text-sm text-gray-500">Hours Logged</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <CheckCircle className="w-8 h-8 mb-2 text-green-600" />
              <p className="text-3xl font-bold text-green-600">
                {volunteerHours.filter(h => h.status === 'approved').reduce((sum, h) => sum + h.hours, 0)}
              </p>
              <p className="text-sm text-gray-500">Hours Approved</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <Calendar className="w-8 h-8 mb-2 text-amber-500" />
              <p className="text-3xl font-bold text-amber-500">
                {employeeUser.pto_given || 0}
              </p>
              <p className="text-sm text-gray-500">PTO Days Given</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border">
              <Gift className="w-8 h-8 mb-2 text-purple-600" />
              <p className="text-3xl font-bold text-purple-600">
                {(employeeUser.pto_given || 0) - (employeeUser.pto_used || 0)}
              </p>
              <p className="text-sm text-gray-500">PTO Days Remaining</p>
            </div>
          </div>
          
          {/* Volunteer Opportunities Section */}
          {volunteerOpportunities.length > 0 && (
            <div className="mb-8">
              <h3 className="font-semibold text-lg mb-4">Upcoming Volunteer Opportunities</h3>
              <div className="grid grid-cols-3 gap-4">
                {volunteerOpportunities.filter(e => e.status === 'upcoming' || e.status === 'ongoing').map((event) => (
                  <div key={event.event_id} className="bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-md transition-all">
                    <div className="h-1.5" style={{ background: primaryColor }} />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{event.title}</h4>
                          {event.charity_name && (
                            <p className="text-sm text-gray-500">{event.charity_name}</p>
                          )}
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          event.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                          event.status === 'ongoing' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {event.status}
                        </span>
                      </div>
                      
                      {event.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{event.description}</p>
                      )}
                      
                      <div className="space-y-1.5 text-sm mb-4">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{event.event_date}</span>
                          {event.start_time && <span>at {event.start_time}</span>}
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{event.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{event.hours_value || 4}h credit</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Users className="w-4 h-4" />
                          <span>{event.registered_count || 0}{event.max_volunteers ? `/${event.max_volunteers}` : ''} registered</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/api/volunteer/events/${event.event_id}/register`, {});
                            alert('Successfully registered for this event!');
                            fetchVolunteerData();
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Failed to register');
                          }
                        }}
                        className="w-full py-2 text-white rounded-lg font-medium text-sm"
                        style={{ background: primaryColor }}
                      >
                        Register
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Volunteer Log History */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Volunteer Activity Log</h3>
            </div>
            <div className="divide-y">
              {volunteerHours.length > 0 ? (
                volunteerHours.map((log, i) => (
                  <div key={i} className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${primaryColor}20` }}>
                      <Clock className="w-5 h-5" style={{ color: primaryColor }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{log.description || 'Volunteer Activity'}</p>
                      <p className="text-sm text-gray-500">{log.charity_name || 'Community Service'}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-lg" style={{ color: primaryColor }}>{log.hours}h</p>
                      <p className="text-xs text-gray-400">Hours</p>
                    </div>
                    <div className="text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        log.status === 'approved' ? 'bg-green-100 text-green-700' :
                        log.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {log.status || 'pending'}
                      </span>
                    </div>
                    <div className="text-right text-xs text-gray-400">
                      {new Date(log.date).toLocaleDateString()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No volunteer hours logged yet</p>
                  <p className="text-sm mt-1">Click &quot;Log Hours&quot; to record your volunteer activities</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* MY MATCHING VIEW */}
      {activeView === 'my-matching' && employeeUser && (
        <section className="py-12 px-4 max-w-5xl mx-auto">
          <EmployeeMatchDashboard 
            primaryColor={primaryColor} 
            onClose={() => setActiveView('home')}
          />
        </section>
      )}

      {/* MY BADGES VIEW */}
      {activeView === 'my-badges' && employeeUser && (
        <section className="py-12 px-4 max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">My Badges</h2>
            <p className="text-gray-500">Your achievements and recognition</p>
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: `${primaryColor}15` }}>
              <Trophy className="w-5 h-5" style={{ color: primaryColor }} />
              <span className="font-bold" style={{ color: primaryColor }}>{myBadges.total_points || 0} Total Points</span>
            </div>
          </div>
          
          {/* Earned Badges */}
          <div className="mb-10">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5" style={{ color: primaryColor }} />
              Earned Badges ({myBadges.earned?.length || 0})
            </h3>
            {myBadges.earned?.length > 0 ? (
              <div className="grid grid-cols-4 gap-4">
                {myBadges.earned.map(badge => (
                  <div key={badge.badge_id} className="bg-white rounded-xl border-2 p-5 text-center shadow-md hover:shadow-lg transition-all"
                    style={{ borderColor: primaryColor }}>
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center text-4xl"
                      style={{ background: `${primaryColor}15` }}>
                      {badge.icon || '🏆'}
                    </div>
                    <p className="font-bold text-gray-900">{badge.name}</p>
                    <p className="text-xs text-gray-500 mb-3">{badge.description}</p>
                    <div className="flex items-center justify-center gap-1 mb-3">
                      <span className="px-2 py-1 rounded-full text-xs font-bold" style={{ background: `${primaryColor}20`, color: primaryColor }}>
                        +{badge.points} pts
                      </span>
                    </div>
                    {/* Share Buttons */}
                    <div className="flex justify-center gap-2">
                      <button 
                        onClick={() => {
                          const text = `I earned the "${badge.name}" badge for ${badge.description}! 🎉`;
                          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-blue-500"
                        title="Share on Twitter"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => {
                          const text = `I earned the "${badge.name}" badge for ${badge.description}! 🎉`;
                          window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&summary=${encodeURIComponent(text)}`, '_blank');
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-blue-700"
                        title="Share on LinkedIn"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                    {badge.earned_at && (
                      <p className="text-xs text-gray-400 mt-2">Earned {new Date(badge.earned_at).toLocaleDateString()}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-500">
                <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No badges earned yet. Keep donating and volunteering!</p>
              </div>
            )}
          </div>
          
          {/* Available Badges */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-600">Available Badges</h3>
            <p className="text-sm text-gray-500 mb-4">Complete these achievements to earn more badges</p>
            {myBadges.available?.length > 0 ? (
              <div className="grid grid-cols-4 gap-4">
                {myBadges.available.slice(0, 8).map(badge => (
                  <div key={badge.badge_id} className="bg-white rounded-xl border p-5 text-center opacity-70 hover:opacity-100 transition-all">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center text-4xl bg-gray-100">
                      {badge.icon || '🏆'}
                    </div>
                    <p className="font-semibold text-gray-700">{badge.name}</p>
                    <p className="text-xs text-gray-500 mb-2">{badge.description}</p>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      +{badge.points} pts
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-500">
                <p>You have earned all available badges! 🎉</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* LOG HOURS MODAL */}
      {showLogHoursModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Log Volunteer Hours</h3>
              <button onClick={() => setShowLogHoursModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newHoursForm.date}
                  onChange={(e) => setNewHoursForm({ ...newHoursForm, date: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours Volunteered</label>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={newHoursForm.hours}
                  onChange={(e) => setNewHoursForm({ ...newHoursForm, hours: e.target.value })}
                  placeholder="e.g., 4"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization/Charity</label>
                <input
                  type="text"
                  value={newHoursForm.charity_name}
                  onChange={(e) => setNewHoursForm({ ...newHoursForm, charity_name: e.target.value })}
                  placeholder="e.g., Local Food Bank"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description of Activity</label>
                <textarea
                  value={newHoursForm.description}
                  onChange={(e) => setNewHoursForm({ ...newHoursForm, description: e.target.value })}
                  placeholder="What did you do?"
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowLogHoursModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!newHoursForm.hours || !newHoursForm.description) {
                    alert('Please fill in hours and description');
                    return;
                  }
                  setSubmittingHours(true);
                  try {
                    await api.post('/api/volunteer/log', {
                      date: newHoursForm.date,
                      hours: parseFloat(newHoursForm.hours),
                      description: newHoursForm.description,
                      charity_name: newHoursForm.charity_name
                    });
                    
                    // Add to local state
                    setVolunteerHours([...volunteerHours, {
                      ...newHoursForm,
                      hours: parseFloat(newHoursForm.hours),
                      status: 'pending'
                    }]);
                    
                    setShowLogHoursModal(false);
                    setNewHoursForm({ date: new Date().toISOString().split('T')[0], hours: '', description: '', charity_name: '' });
                    alert('Hours logged successfully! Pending approval.');
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Failed to log hours');
                  } finally {
                    setSubmittingHours(false);
                  }
                }}
                disabled={submittingHours}
                className="px-4 py-2 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                style={{ background: primaryColor }}
              >
                {submittingHours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {submittingHours ? 'Submitting...' : 'Log Hours'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DONATE MODAL */}
      {showDonateModal && (
        <DonationModal
          primaryColor={primaryColor}
          business={business}
          selectedCampaign={selectedCampaign}
          donationAmount={donationAmount}
          setDonationAmount={setDonationAmount}
          customAmount={customAmount}
          setCustomAmount={setCustomAmount}
          donorEmail={donorEmail}
          setDonorEmail={setDonorEmail}
          donorName={donorName}
          setDonorName={setDonorName}
          isEmployee={isEmployee}
          setIsEmployee={setIsEmployee}
          employeeVerified={employeeVerified}
          setEmployeeVerified={setEmployeeVerified}
          employeeUser={employeeUser}
          matchInfo={matchInfo}
          verifying={verifying}
          processing={processing}
          donationFlowStep={donationFlowStep}
          setDonationFlowStep={setDonationFlowStep}
          handleDonate={handleDonate}
          verifyEmployee={verifyEmployee}
          setShowDonateModal={setShowDonateModal}
          setShowLoginModal={setShowLoginModal}
        />
      )}

      {/* EMPLOYEE LOGIN MODAL */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Employee Sign In</h3>
              <button onClick={() => setShowLoginModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={donorEmail}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  placeholder="Enter your password"
                />
              </div>
              
              {loginError && (
                <p className="text-sm text-red-600">{loginError}</p>
              )}
              
              <button
                onClick={handleEmployeeLogin}
                className="w-full py-3 text-white rounded-lg font-semibold"
                style={{ background: primaryColor }}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DIRECT EMPLOYEE LOGIN MODAL */}
      {showDirectLoginModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Employee Sign In</h3>
                <p className="text-sm text-gray-500">Sign in to track your impact</p>
              </div>
              <button 
                onClick={() => {
                  setShowDirectLoginModal(false);
                  setDirectLoginEmail('');
                  setDirectLoginPassword('');
                  setDirectLoginError('');
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                &times;
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Email</label>
                <input
                  type="email"
                  value={directLoginEmail}
                  onChange={(e) => setDirectLoginEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={directLoginPassword}
                  onChange={(e) => setDirectLoginPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your password"
                  onKeyPress={(e) => e.key === 'Enter' && handleDirectLogin()}
                />
              </div>
              
              {directLoginError && (
                <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{directLoginError}</p>
              )}
              
              <button
                onClick={handleDirectLogin}
                className="w-full py-3 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
                style={{ background: primaryColor }}
              >
                <User className="w-4 h-4" />
                Sign In
              </button>
              
              <p className="text-xs text-center text-gray-500">
                Use the credentials provided by your employer
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            &copy; {new Date().getFullYear()} {business.name}. Powered by{' '}
            <a href="https://altrunic.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
              Altrunic
            </a>
            {' '}&mdash; the free corporate giving platform.
          </p>
        </div>
      </footer>

      {/* Embedded Stripe Donation Modal */}
      <StripeDonationModal
        isOpen={showStripeDonation}
        onClose={() => { setShowStripeDonation(false); setStripeDonationCharity(null); }}
        charity={stripeDonationCharity}
        campaign={selectedCampaign}
        businessId={businessId}
        donorType={isEmployee && employeeVerified ? 'employee' : 'guest'}
        matchInfo={matchInfo}
        onSuccess={handleStripeDonationSuccess}
      />
    </div>
  );
};

export default ImpactPortal;
